import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search, Sparkles, Check } from 'lucide-react'
import { fieldTemplatesApi, type FieldTemplate } from '@/api/fieldTemplates'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

interface Props {
  selectedId?: string | null
  onSelect: (template: FieldTemplate | null) => void
  /** When true, shows a "Skip / start blank" option */
  allowSkip?: boolean
}

export function TemplatePicker({ selectedId, onSelect, allowSkip = false }: Props) {
  const [query, setQuery] = useState('')
  const [activeCategory, setActiveCategory] = useState<string | null>(null)

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['field-templates'],
    queryFn: fieldTemplatesApi.list,
  })

  const categories = useMemo(() => {
    const set = new Set<string>()
    templates.forEach((t) => t.category && set.add(t.category))
    return Array.from(set).sort()
  }, [templates])

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      if (activeCategory && t.category !== activeCategory) return false
      if (query) {
        const q = query.toLowerCase()
        const inName = t.name.toLowerCase().includes(q)
        const inDesc = (t.description || '').toLowerCase().includes(q)
        const inFields = t.fields.some((f) =>
          f.name.toLowerCase().includes(q) ||
          (f.display_name || '').toLowerCase().includes(q)
        )
        if (!inName && !inDesc && !inFields) return false
      }
      return true
    })
  }, [templates, query, activeCategory])

  const groupedByCategory = useMemo(() => {
    const groups: Record<string, FieldTemplate[]> = {}
    filtered.forEach((t) => {
      const cat = t.category || 'Other'
      if (!groups[cat]) groups[cat] = []
      groups[cat].push(t)
    })
    return groups
  }, [filtered])

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search templates..."
          className="pl-9"
        />
      </div>

      {/* Category pills */}
      {categories.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveCategory(null)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              activeCategory === null
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              type="button"
              key={cat}
              onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                activeCategory === cat
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Skip option */}
      {allowSkip && (
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={`w-full text-left p-3 rounded-lg border transition-colors ${
            selectedId === null || selectedId === undefined
              ? 'border-blue-600 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Start with a blank project</div>
              <div className="text-xs text-muted-foreground">Define your own fields from scratch</div>
            </div>
            {(selectedId === null || selectedId === undefined) && (
              <Check className="h-4 w-4 text-blue-600" />
            )}
          </div>
        </button>
      )}

      {/* Templates */}
      {isLoading ? (
        <div className="text-center py-8 text-sm text-muted-foreground">Loading templates...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-8 text-sm text-muted-foreground">
          No templates match your search.
        </div>
      ) : (
        <div className="space-y-4 max-h-[420px] overflow-y-auto pr-1">
          {Object.entries(groupedByCategory).map(([cat, items]) => (
            <div key={cat}>
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{cat}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((t) => {
                  const isSelected = selectedId === t.id
                  return (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => onSelect(t)}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        isSelected
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-sm font-medium truncate">{t.name}</span>
                            {!t.is_system && (
                              <Badge variant="secondary" className="text-[10px] py-0 px-1.5">Custom</Badge>
                            )}
                          </div>
                          {t.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                              {t.description}
                            </p>
                          )}
                          <div className="text-[11px] text-muted-foreground">
                            {t.fields.length} field{t.fields.length !== 1 ? 's' : ''}
                            {t.fields.some((f) => f.is_line_item) && ' · multi-row'}
                          </div>
                        </div>
                        {isSelected && (
                          <Check className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


/**
 * A modal-style picker that returns the selected template via callback.
 * Used by FieldManager (Apply Template button).
 */
interface ApplyTemplateDialogContentProps {
  onApply: (templateId: string, replaceExisting: boolean) => void | Promise<void>
  onCancel: () => void
  isApplying?: boolean
  hasExistingFields?: boolean
  error?: string | null
}

export function ApplyTemplateDialogContent({
  onApply,
  onCancel,
  isApplying,
  hasExistingFields,
  error,
}: ApplyTemplateDialogContentProps) {
  const [selected, setSelected] = useState<FieldTemplate | null>(null)
  const [replaceExisting, setReplaceExisting] = useState(false)

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Sparkles className="h-4 w-4 text-blue-600" />
        Pick a template — fields will be added to your project. Edit them after.
      </div>

      <TemplatePicker
        selectedId={selected?.id}
        onSelect={(t) => setSelected(t)}
      />

      {hasExistingFields && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={replaceExisting}
            onChange={(e) => setReplaceExisting(e.target.checked)}
          />
          <span>Replace my existing fields (otherwise template fields are appended)</span>
        </label>
      )}

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2 border-t">
        <Button variant="outline" onClick={onCancel} disabled={isApplying}>
          Cancel
        </Button>
        <Button
          disabled={!selected || isApplying}
          onClick={() => selected && onApply(selected.id, replaceExisting)}
        >
          {isApplying ? 'Applying...' : selected ? `Apply "${selected.name}"` : 'Select a template'}
        </Button>
      </div>
    </div>
  )
}
